import React, { useEffect, useRef, useState } from 'react';
import DatePicker from '../Components/DatePicker/DatePicker';
import axiosInstance from '../Axios/AxiosInstance';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Header from '../Components/Header/Header';
import { MdDownload, MdFilterAltOff } from 'react-icons/md';
import Footer from '../Components/Footer/Footer';
import ReusableTable from '../Components/Table/ReusableTable';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BsCalendar3 } from "react-icons/bs";

const Dashboard = () => {

  const navigate = useNavigate();
  const popupRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState({ startDate: '', endDate: '' });
  const [dashboardCounts, setDashboardCounts] = useState({
    tokenScannedCount: 0,
    totalPainterCount: 0,
    totalDealerCount: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const cards = [
    { text: 'Tokens Scanned Value', count: dashboardCounts.tokenScannedCount },
    { text: 'Active Painters', count: dashboardCounts.totalPainterCount },
    { text: 'Active Dealers', count: dashboardCounts.totalDealerCount },
    {
      text: 'CN Pending Value',
      count: dashboardCounts.totalCNPendingValueCount !== null
        ? `₹ ${dashboardCounts.totalCNPendingValueCount}`
        : '-'
    },
  ];
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageDataRef = useRef({});

  const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  };
  const formatDateDisplay = (date) => {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  };

  const getCurrentDate = () => {
    const date = new Date();
    return formatDate(date);
  };

  useEffect(() => {
    const handlePopState = () => {
      const userinfo = JSON.parse(sessionStorage.getItem('userinfo'));
      if (!userinfo) {
        navigate('/');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowDatePicker(false)
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchInput === '') {
      pageDataRef.current = {};
      fetchTableData();
    }
  }, [searchInput]);

  useEffect(() => {
    fetchTableData();
  }, [selectedDates, rowsPerPage, currentPage, searchInput]);

  useEffect(() => {
    fetchDashboardCounts();
  }, [selectedDates]);

  const fetchTableData = () => {

    const cacheKey = `${currentPage}-${rowsPerPage}-${selectedDates.startDate || ""}-${selectedDates.endDate || ""}-${searchInput}`;

    if (pageDataRef.current[cacheKey]) {
      setTableData(pageDataRef.current[cacheKey]);
      setIsLoading(false);
    } else {
      setIsLoading(true);

      axiosInstance
        .post("admindashboard/DashBordDataListByDay", {
          fromDate: selectedDates.startDate,
          toDate: selectedDates.endDate,
          dealerId: 0,
          painterId: 0,
          searchItem: searchInput,
          pageNumber: currentPage,
          pageSize: rowsPerPage,
        })
        .then((response) => {
          const { adminDayDataList, paginatedData } = response.data;
          const formattedData = adminDayDataList.map((item) => ({
            scannedDate: new Date(item.scannedDate).toLocaleDateString("en-GB"),
            dealerName: item.dealerName,
            dealerCode: item.dealerCode.replace(/^0+/, ''),
            dealerId: item.dealerId,
            creditNoteNumber: item.creditNoteNumer || "-",
            totalAmount: item.totalAmount,
            status: item.sapStatus,
            city: item.city,
            region: item.region,
            handlingCharges: item.handlingCharges,
            totalFinalAmount: item.totalFinalAmount,
          }));

          setTableData(formattedData);
          setTotalRecords(paginatedData.totalRecords);
          // pageDataRef.current[cacheKey] = formattedData;
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("Error fetching table data:", error);
        });
    }
  };

  const columns = [
    { key: "dealerCode", label: "Dealer Code" },
    { key: "dealerName", label: "Dealer Name" },
    { key: "scannedDate", label: "Date" },
    { key: "creditNoteNumber", label: "CN Number" },
    { key: "status", label: "Status" },
    { key: "city", label: "City" },
    { key: "region", label: "State" },
    { key: "totalAmount", label: "Total Amount", isTextAlignRight: true, isCurrency: true },
    { key: "handlingCharges", label: "Handling Charges", isTextAlignRight: true, isCurrency: true },
    { key: "totalFinalAmount", label: "Total Final Amount", isTextAlignRight: true, isCurrency: true },
  ];

  const fetchDashboardCounts = () => {
    axiosInstance.post('admindashboard/DashboardCountData', {
      fromDate: selectedDates.startDate,
      toDate: selectedDates.endDate,
      dealerId: 0,
      painterId: 0,
      searchItem: "",
      pageNumber: 0,
      pageSize: 0,
    })
      .then(response => {
        setDashboardCounts(response.data);
      })
      .catch(error => {
        console.error('Error fetching dashboard counts:', error);
      });
  };

  const handleDateSubmit = (dates) => {
    const formattedDates = {
      startDate: formatDate(dates.startDate),
      endDate: formatDate(dates.endDate),
    };
    setSelectedDates(formattedDates);
    setShowDatePicker(false);
  };

  const exportToCSV = (csvData, fileName) => {
    const columnMapping = {
      dealerCode: "Dealer Code",
      dealerName: "Dealer Name",
      painterName: "Painter Name",
      painterMobile: "Painter Mobile",
      scannedDate: "Scanned Date",
      productName: "Product Name",
      uniqueCode: "Unique Code",
      cnNumber: "CN Number",
      cnDate: "CN Date",
      meterialCode: "Material Code",
      batchORder: "Batch Order",
      referenceId: "Reference Id",
      processOrderNumber: "Process Order Number",
      city: "City",
      region: "Region",
      tokenValue: "Token Value",
      handlingCharges: "Handling Charges",
      totalFinalAmount: "Total Amount",
      schemeName: "SchemeName",
      schemeAmount: "SchemeAmount"
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${dd}-${mm}-${yyyy} ${hours}:${minutes}:${seconds}`;
    };

    const formattedData = csvData.map(item => {
      const formattedItem = {};

      for (const key in item) {
        if (columnMapping[key] && !['tokenValue', 'handlingCharges', 'totalFinalAmount'].includes(key)) {
          if (key === 'scannedDate' || key === 'cnDate') {
            formattedItem[columnMapping[key]] = formatDate(item[key]);
          } else {
            formattedItem[columnMapping[key]] = item[key];
          }
        }
      }

      ['tokenValue', 'handlingCharges', 'totalFinalAmount'].forEach(key => {
        if (item[key] !== undefined) {
          formattedItem[columnMapping[key]] = item[key];
        }
      });

      return formattedItem;
    });

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `${fileName}.xlsx`);
  };

  const handleDownload = () => {
    axiosInstance.post('admindashboard/DashboardDownloadList', {
      fromDate: selectedDates.startDate,
      toDate: selectedDates.endDate,
      dealerId: 0,
      painterId: 0,
      searchItem: "",
      pageNumber: 0,
      pageSize: 0,
    }, {
      responseType: 'json',
    })
      .then(response => {
        const data = response.data;

        if (!data || data.length === 0) {
          toast.error('There is no data in the selected date range');
          return;
        }

        const currentDate = getCurrentDate();
        exportToCSV(data, `QRScanReport_${currentDate}`);
      })
      .catch(error => {
        console.error('Error downloading data:', error);
        toast.error('Failed to download data');
      });
  };

  const clearFilters = () => {
    setSelectedDates({ startDate: '', endDate: '' });
    setSearchInput('');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSelectedRowsChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleEyeClick = (row) => {
    const { dealerId, scannedDate, dealerName, dealerCode } = row;
    navigate(`/dealer-flow/${dealerId}`, { state: { scannedDate, dealerName, dealerCode } });
  };

  const handleSearchInputChange = (e) => {
    const newSearchValue = e.target.value;
    setSearchInput(newSearchValue);
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title={"Dashboard"} />
      <div className="flex flex-col flex-1 mx-4 mt-3">
        <div className="flex flex-wrap gap-4 justify-between mb-4">
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-[#1e365e] text-white p-2.5 rounded-lg text-center flex-1 min-w-[150px] sm:min-w-[200px] md:flex-auto mx-auto sm:mx-2"
            >
              <div className="text-base md:text-lg tracking-wide">
                {card.text}: {card.count === null || card.count === '' ? '-' : card.count}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-between items-center mb-2">
          <div className="flex items-center gap-4">
            <input
              type="text"
              placeholder="Search by Dealer Name/Code..."
              className="px-4 py-2 border border-gray-300 rounded-lg w-[300px] focus:outline-none focus:ring-2 focus:ring-black-500"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {selectedDates.startDate && (
              <div className="ml-2 flex space-x-3">
                <span className="bg-[#1e365e] text-white px-3 py-2 rounded-lg shadow-md">
                  From: {formatDateDisplay(selectedDates.startDate)}
                </span>
                <span className="bg-[#1e365e] text-white px-4 py-2 rounded-lg shadow-md ">
                  To: {formatDateDisplay(selectedDates.endDate)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
               onClick={clearFilters}
              title="Clear Filter"
            >
              <MdFilterAltOff  size={32} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={() => setShowDatePicker(!showDatePicker)}
              title="Date Filter"
            >
              <BsCalendar3 size={25} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={handleDownload}
              title="Download"
            >
              <MdDownload size={32} />
            </span>
          </div>
        </div>

        <div className="p-1">
          <ReusableTable
            data={tableData}
            columns={columns}
            rowsPerPage={rowsPerPage}
            loading={isLoading}
            currentPage={currentPage}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectedRowsChange={handleSelectedRowsChange}
            enableEye={true}
            onEyeClick={handleEyeClick}
          />
        </div>

        {showDatePicker && (
          <dialog open
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "none",
              background: "none",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
            ref={popupRef}>
            <div >
              <DatePicker onSubmit={handleDateSubmit} onCancel={() => setShowDatePicker(false)} />
            </div>
          </dialog>
        )}

      </div>
      <Footer />
      <ToastContainer />
    </div>
  );
};

export default Dashboard;
